import type { FormikErrors } from 'formik';
import {
  TOWPRO_TOW_REASONS,
  type TowProDispatchRequest,
  type TowProRequestResponse,
  type TowProVehicleOwnerData,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { VIN_LENGTH, type TowProFormValues } from './towProFormConstants';

export const REQUIRED_ERROR = 'Required';
export const ADDRESS_REQUIRED_ERROR = 'Address is required';
export const AGENCY_ID_ERROR = 'Agency ID must be a number';

export const VIN_EXCLUDED_CHARS_WARNING = 'VIN contains I, O, or Q';
export const VIN_INVALID_CHARS_WARNING = 'VIN contains invalid characters';
export const VIN_TOO_LONG_WARNING = 'VIN too long';
export const VIN_PRE_STANDARD_WARNING = `VIN shorter than ${VIN_LENGTH} characters`;
export const VIN_INCOMPLETE_WARNING = 'VIN incomplete';
export const VIN_CHECK_DIGIT_WARNING = "VIN check digit doesn't match";

/** Pre-1981 VINs were not standardized and can be as short as 11 characters. */
const PRE_STANDARD_MIN_VIN_LENGTH = 11;

/** I, O, and Q are excluded from VINs to avoid confusion with 1 and 0. */
const VIN_EXCLUDED_CHARS = /[IOQ]/;
const VIN_VALID_CHARS = /^[A-HJ-NPR-Z0-9]+$/;

/**
 * Normalize a VIN the way it is displayed and stored: uppercase, with the
 * whitespace and dashes that come along when one is copied from a title
 * document (e.g. "1HGCM82633A-004352") stripped out.
 */
export function normalizeVin(value: string): string {
  return value.toUpperCase().replace(/[\s-]/g, '');
}

const VIN_CHECK_DIGIT_TRANSLITERATION: Record<string, number> = {
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
  F: 6,
  G: 7,
  H: 8,
  J: 1,
  K: 2,
  L: 3,
  M: 4,
  N: 5,
  P: 7,
  R: 9,
  S: 2,
  T: 3,
  U: 4,
  V: 5,
  W: 6,
  X: 7,
  Y: 8,
  Z: 9,
};
const VIN_CHECK_DIGIT_WEIGHTS = [
  8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2,
];

/**
 * The 9th character of a standard VIN is a check digit: a weighted sum of
 * the other characters mod 11 (10 is written as "X"). Comparing it catches
 * most single-character transcription errors without any API call.
 */
function vinCheckDigit(vin: string): string {
  let sum = 0;
  for (let i = 0; i < VIN_LENGTH; i++) {
    const char = vin[i];
    const value = /\d/.test(char)
      ? Number(char)
      : VIN_CHECK_DIGIT_TRANSLITERATION[char];
    sum += value * VIN_CHECK_DIGIT_WEIGHTS[i];
  }
  const remainder = sum % 11;
  return remainder === 10 ? 'X' : String(remainder);
}

/**
 * Advisory validation for an already-normalized VIN. Returns a warning to
 * show the user, or null when the VIN looks plausible. Warnings never block
 * submission: tow requests legitimately involve older vehicles, damaged
 * plates, and equipment with nonstandard VINs, and some manufacturers don't
 * follow the North American check-digit convention.
 */
export function getVinWarning(vin: string): string | null {
  if (vin.length === 0) {
    return null;
  }
  if (VIN_EXCLUDED_CHARS.test(vin)) {
    return VIN_EXCLUDED_CHARS_WARNING;
  }
  if (!VIN_VALID_CHARS.test(vin)) {
    return VIN_INVALID_CHARS_WARNING;
  }
  if (vin.length > VIN_LENGTH) {
    return VIN_TOO_LONG_WARNING;
  }
  if (vin.length === VIN_LENGTH) {
    return vinCheckDigit(vin) === vin[8] ? null : VIN_CHECK_DIGIT_WARNING;
  }
  if (vin.length >= PRE_STANDARD_MIN_VIN_LENGTH) {
    return VIN_PRE_STANDARD_WARNING;
  }
  return VIN_INCOMPLETE_WARNING;
}

export function validateTowProForm(
  values: TowProFormValues,
): FormikErrors<TowProFormValues> {
  const errors: FormikErrors<TowProFormValues> = {};

  if (
    values.address.trim().length === 0 ||
    values.latitude == null ||
    values.longitude == null
  ) {
    errors.address = ADDRESS_REQUIRED_ERROR;
  }
  if (!values.region_id) {
    errors.region_id = REQUIRED_ERROR;
  }
  if (!values.reason) {
    errors.reason = REQUIRED_ERROR;
  }
  if (
    values.agency_id.trim().length > 0 &&
    !/^\d+$/.test(values.agency_id.trim())
  ) {
    errors.agency_id = AGENCY_ID_ERROR;
  }

  return errors;
}

const orNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

function buildVehicleOwnerData(
  values: TowProFormValues,
): TowProVehicleOwnerData | null {
  const ownerData: TowProVehicleOwnerData = {
    firstName: orNull(values.owner_first_name),
    lastName: orNull(values.owner_last_name),
    street: orNull(values.owner_street),
    apt: orNull(values.owner_apt),
    city: orNull(values.owner_city),
    state: orNull(values.owner_state),
    zipCode: orNull(values.owner_zip),
  };

  const hasOwnerData = Object.values(ownerData).some(value => value !== null);
  return hasOwnerData ? ownerData : null;
}

/**
 * Map an existing tow request back to editable form values, the inverse of
 * {@link buildTowProDispatchRequest}.
 */
export function formValuesFromRequest(
  request: TowProRequestResponse,
): TowProFormValues {
  const data = request.request.data;
  const owner = data.vehicleOwnerData;

  return {
    address: data.addressInput.address1 ?? '',
    latitude: data.addressInput.location.latitude,
    longitude: data.addressInput.location.longitude,
    region_id: String(data.regionId),
    reason: data.reason,
    related_call: request.callerId ?? '',
    vin: data.vin ?? '',
    make: data.make ?? '',
    model: data.model ?? '',
    vehicle_year: data.vehicleYear ?? '',
    color: data.color ?? '',
    license_plate_number: data.licensePlateNumber ?? '',
    license_plate_state: data.licensePlateState ?? '',
    incident_number: data.incidentNumber ?? '',
    agency_id: data.agencyId != null ? String(data.agencyId) : '',
    officer_name: data.officerName ?? '',
    badge_number: data.badgeNumber ?? '',
    owner_first_name: owner?.firstName ?? '',
    owner_last_name: owner?.lastName ?? '',
    owner_phone: data.addressInput.phone ?? '',
    owner_street: owner?.street ?? '',
    owner_apt: owner?.apt ?? '',
    owner_city: owner?.city ?? '',
    owner_state: owner?.state ?? '',
    owner_zip: owner?.zipCode ?? '',
    notes: data.notes ?? '',
  };
}

/**
 * Map validated form values to the TowPro dispatch payload. Returns null when
 * the location or reason has not been resolved yet, which validation prevents
 * before submission.
 */
export function buildTowProDispatchRequest(
  values: TowProFormValues,
): TowProDispatchRequest | null {
  const reason = TOWPRO_TOW_REASONS.find(known => known === values.reason);
  if (values.latitude == null || values.longitude == null || !reason) {
    return null;
  }

  return {
    regionId: Number(values.region_id),
    addressInput: {
      address1: orNull(values.address),
      // TowPro keeps the vehicle owner's phone on the pickup address.
      phone: orNull(values.owner_phone),
      location: { latitude: values.latitude, longitude: values.longitude },
    },
    reason,
    sentToRotation: true,
    notes: orNull(values.notes),
    licensePlateNumber: orNull(values.license_plate_number),
    licensePlateState: orNull(values.license_plate_state),
    make: orNull(values.make),
    model: orNull(values.model),
    color: orNull(values.color),
    vehicleYear: orNull(values.vehicle_year),
    incidentNumber: orNull(values.incident_number),
    agencyId:
      values.agency_id.trim().length > 0 ? Number(values.agency_id) : null,
    vin: orNull(values.vin),
    officerName: orNull(values.officer_name),
    badgeNumber: orNull(values.badge_number),
    vehicleOwnerData: buildVehicleOwnerData(values),
  };
}
